import React, { useState, useEffect } from "react";
import useSWR from "swr";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import TopActionMenus from "./TopActionMenus";
import DashboardContentFooter from "./DashboardContentFooter";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { useNavigationEvent } from "navigation-react";
import SecureLS from "secure-ls";

const ls = new SecureLS({ encodingType: "aes" });
const API_BASE = process.env.REACT_APP_DRUPAL_BASE_URL;

// SWR fetcher
const fetcher = (url) =>
  fetch(url, { credentials: "include" }).then((res) => {
    if (!res.ok) throw new Error(`Fetch error ${res.status}`);
    return res.json();
  });

// Get auth headers
const getAuthHeaders = () => {
  const csrfToken = ls.get("usercsrf_token");
  if (!csrfToken) throw new Error("No CSRF token found");
  return {
    Accept: "application/vnd.api+json",
    "X-CSRF-Token": csrfToken,
  };
};

export default function StaffAddRegister() {
  const { stateNavigator } = useNavigationEvent();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [department, setDepartment] = useState("");
  const [statusID, setStatusID] = useState(1);
  const [staffPicture, setStaffPicture] = useState(null);
  const [staffID, setStaffID] = useState("");
  const [staffPreview, setStaffPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const DEPARTMENT_API = `${API_BASE}/jsonapi/taxonomy_term/staff_department`;
  const STAFF_POST_API = `${API_BASE}/jsonapi/user/user`;
  const FILE_UPLOAD_API = `${API_BASE}/file/upload/user/field_staff_picture`;

  const { data: departmentData } = useSWR(DEPARTMENT_API, fetcher);
  const departmentItems =
    departmentData?.data?.map((term) => ({
      label: term.attributes.name,
      value: term.id,
    })) || [];

  useEffect(() => {
    const csrfToken = ls.get("usercsrf_token");
    const username = ls.get("username");
    if (!csrfToken || !username) {
      alert("You are not logged in. Redirecting to login page.");
      stateNavigator.navigate("login");
    }
  }, [stateNavigator]);

  // Handle image preview
  const handlePictureChange = (file) => {
    setStaffPicture(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setStaffPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setStaffPreview(null);
    }
  };

  // Upload staff picture using FormData
  const uploadStaffPicture = async (file) => {
    if (!file) return null;
    const csrfToken = ls.get("usercsrf_token");

    const formData = new FormData();
    formData.append("files[field_staff_picture_0]", file, file.name);

    const res = await fetch(FILE_UPLOAD_API, {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken,
      },
      credentials: "include",
      body: formData,
    });

    if (!res.ok) {
      const errText = await res.text();
      throw new Error(`File upload failed: ${errText}`);
    }

    const json = await res.json();
    return json.data[0]?.id; // UUID of uploaded file
  };

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setDepartment("");
    setStatusID(1);
    setStaffPicture(null);
    setStaffPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!firstName || !lastName || !department) {
      alert("Please fill all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const headers = getAuthHeaders();

      // Generate username as firstname.lastname
      const username = `${firstName.toLowerCase()}.${lastName.toLowerCase()}`;

      const payload = {
        data: {
          type: "user--user",
          attributes: {
            name: username,
            field_firist_name: firstName,
            field_last_name: lastName,
            field_email_address: email,
            field_phone_number: phone,
            status: statusID,
            field_staff_id: staffID,
          },
          relationships: {
            field_department: {
              data: { type: "taxonomy_term--staff_department", id: department },
            },
            roles: {
              data: [
                {
                  type: "user_role--user_role",
                  id: "08ec0c48-dfbf-4fc3-ad8e-2d13db175bbc",
                },
                {
                  type: "user_role--user_role",
                  id: "3e8ff831-f6ee-4773-8d60-94d7d5971f24",
                },
              ],
            },
          },
        },
      };

      // Upload file if selected
      if (staffPicture) {
        const fileID = await uploadStaffPicture(staffPicture);
        payload.data.relationships.field_staff_picture = {
          data: { type: "file--file", id: fileID },
        };
      }

      const response = await fetch(STAFF_POST_API, {
        method: "POST",
        headers: {
          ...headers,
          "Content-Type": "application/vnd.api+json",
        },
        body: JSON.stringify(payload),
        credentials: "include",
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`Failed to add staff: ${errText}`);
      }

      const result = await response.json();
      console.log("Staff added successfully:", result);
      alert("Staff added successfully");

      // Reset form after successful submission
      resetForm();

      stateNavigator.navigate("staffregister");
    } catch (err) {
      console.error("Error adding staff:", err);
      alert(`Error adding staff: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div className="patient-register">
          <TopActionMenus
            AddTitle="Add New Staff"
            backIcon={faArrowLeft}
            backNavigationLink="staffregister"
          />
          <form
            className="patient-form"
            onSubmit={handleSubmit}
            style={{ width: "60%", margin: "0 auto" }}
          >
            <div className="form-group">
              <label>Staff ID</label>
              <input
                type="text"
                value={staffID}
                maxLength={4}
                onChange={(e) => setStaffID(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>First Name</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label>Last Name</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Phone Number</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Department</label>
              <select
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                required
              >
                <option value="">Select Department</option>
                {departmentItems.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Staff Picture</label>
              <input
                type="file"
                onChange={(e) => handlePictureChange(e.target.files[0])}
              />
              {staffPreview && (
                <div style={{ marginTop: "10px" }}>
                  <img
                    src={staffPreview}
                    alt="Preview"
                    style={{ maxWidth: "200px", borderRadius: "5px" }}
                  />
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Status</label>
              <select
                value={statusID}
                onChange={(e) => setStatusID(Number(e.target.value))}
                required
              >
                <option value="">Select Status</option>
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>

            <button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Adding..." : "Add Staff"}
            </button>
          </form>
        </div>
      </div>
      <DashboardContentFooter />
    </div>
  );
}
